/**
 * Theme (light / dark / system) — CHAP-154.
 *
 * Persists to localStorage, applies a `dark` class to <html>, and follows the
 * OS preference in 'system' mode. Default is 'light' so existing users see no
 * change until they opt in. Dark styling is a scoped utility-override layer in
 * index.css (`.dark .content-surface ...`) so it covers all pages without
 * per-component edits; the navy sidebar is intentionally always dark.
 *
 * v1: full per-component dark polish is incremental (the ticket is a Phase-2
 * placeholder). The override layer gives coherent dark across the content area.
 */
import { useState, useEffect, useCallback } from 'react';

export type Theme = 'light' | 'dark' | 'system';
const KEY = 'chapterbuild_theme';

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

export function getStoredTheme(): Theme {
  if (typeof localStorage === 'undefined') return 'light';
  const v = localStorage.getItem(KEY);
  return v === 'dark' || v === 'light' || v === 'system' ? v : 'light';
}

/** Resolve + apply the effective theme to <html>. Safe to call pre-React. */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const dark = theme === 'dark' || (theme === 'system' && systemPrefersDark());
  document.documentElement.classList.toggle('dark', dark);
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow OS changes while in 'system' mode.
  useEffect(() => {
    if (theme !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    try { localStorage.setItem(KEY, t); } catch { /* ignore */ }
    setThemeState(t);
  }, []);

  // Cycle light → dark → system → light.
  const cycleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light');
  }, [theme, setTheme]);

  return { theme, setTheme, cycleTheme };
}
